import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookieConsentProps {
  onAccept: (preferences: {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
  }) => void;
}

export default function CookieConsent({ onAccept }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made cookie choices
    const cookieChoices = localStorage.getItem('cookie-preferences');
    if (!cookieChoices) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allPreferences);
    onAccept(allPreferences);
    localStorage.setItem('cookie-preferences', JSON.stringify(allPreferences));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    onAccept(preferences);
    localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-gray-800 border-t border-gray-700"
      >
        <div className="max-w-7xl mx-auto p-4">
          {showPreferences ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Cookie className="h-6 w-6 text-blue-400 mr-2" />
                  <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-white"
                  aria-label="Close cookie preferences"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Essential Cookies</p>
                    <p className="text-sm text-gray-300">Required for basic functionality</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.essential}
                    disabled
                    className="h-4 w-4"
                    aria-label="Essential cookies (required)"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Analytics Cookies</p>
                    <p className="text-sm text-gray-300">Help us improve our service</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="h-4 w-4"
                    aria-label="Analytics cookies"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white">Marketing Cookies</p>
                    <p className="text-sm text-gray-300">Help us show relevant content</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="h-4 w-4"
                    aria-label="Marketing cookies"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center">
                <Cookie className="h-6 w-6 text-blue-400 mr-2 flex-shrink-0" />
                <p className="text-gray-300 text-sm">
                  We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.
                  Read our{' '}
                  <Link to="/privacy-policy" className="text-blue-400 hover:text-blue-300">
                    Privacy Policy
                  </Link>
                  {' '}and{' '}
                  <Link to="/terms-of-service" className="text-blue-400 hover:text-blue-300">
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowPreferences(true)}
                  className="text-gray-300 hover:text-white text-sm"
                >
                  Cookie Settings
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}